import { useTranslate } from '@tolgee/react';
import { SeasonFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import SelectField from '@amf/shared/components/form/SelectField';
import { findTeamDefaultLeague } from '@amf/shared/data/Team';

export type SeasonSelectData = {
  season: string;
  league: string;
};

type Props = {
  values: SeasonSelectData;
  seasons: SeasonFragment[];
  onUpdate: (data: SeasonSelectData) => Promise<void>;
  team?: TeamListItemFragment;
  allSeasonsAllowed?: boolean;
  allLeaguesAllowed?: boolean;
};

function SeasonSelect({
  seasons,
  values,
  onUpdate,
  team,
  allSeasonsAllowed,
  allLeaguesAllowed,
}: Props) {
  const { t } = useTranslate();

  const [data, setData] = useState<SeasonSelectData>({ season: '-', league: '-' });
  useEffect(() => setData(values), [values]);

  const onSeasonChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    async e => {
      const seasonId = e.target.value;
      const season = seasons.find(s => s.id === seasonId);
      if (!season && !allSeasonsAllowed) return;
      if (!season && allSeasonsAllowed) {
        setData({ season: seasonId, league: '-' });
        await onUpdate({ season: seasonId, league: '-' });
        return;
      }

      let league = findTeamDefaultLeague(team, season);
      if (!league) league = season.leagues[0];

      setData({ season: seasonId, league: league.id });
      await onUpdate({ season: seasonId, league: league.id });
    },
    [allSeasonsAllowed, onUpdate, seasons, team],
  );

  const onLeagueChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
    async e => {
      const leagueId = e.target.value;
      setData({ season: data.season, league: leagueId });
      await onUpdate({ season: data.season, league: leagueId });
    },
    [data.season, onUpdate],
  );

  const selectedSeason = seasons.find(s => s.id === data.season);
  return (
    <div className='TeamMatches__filter'>
      <SelectField
        className='TeamMatches__field'
        label={t('team.matches.season')}
        onChange={onSeasonChange}
        value={data.season}
      >
        {seasons.map(({ id, name }) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
        {allSeasonsAllowed && <option value={'-'}>{t('team.stats.filter.season.all')}</option>}
      </SelectField>
      <SelectField
        className='TeamMatches__field'
        label={t('team.matches.league')}
        onChange={onLeagueChange}
        value={data.league}
        disabled={!selectedSeason}
      >
        {selectedSeason?.leagues.map(({ id, name }) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
        {allLeaguesAllowed && <option value={'-'}>{t('team.stats.filter.league.all')}</option>}
      </SelectField>
    </div>
  );
}

export default SeasonSelect;
